import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Button,
  Spin,
  Switch,
  Tooltip,
} from "antd";
import { svg } from "../../../../Assets/svg";
import { useSearchParams } from "react-router-dom";
import { renderFormLabel } from "../../../../Components/Components";
import S3Uploader from "../../../../Components/S3Uploader";
import {
  getCurrencyOptions,
  getFullAwsUrl,
  ROLE,
  getStaticSalespersonOptions,
} from "../../../../helpers/utils";
import { useAppDispatch } from "../../../../store";
import { getRolesListAPI, getSageTaxesAPI } from "../clientDistributorsApis";
import {
  createTeamMemberAPI,
  updateTeamMemberAPI,
  getTeamMembersListAPI,
} from "../../TeamMembers/teamMembersApis";
import {
  decryptByText,
  getDecryptedName,
  getDecryptedUserData,
  isAdminSubAdminLoggedIn,
  isCustomerLoggedIn,
} from "../../../../helpers/bycrypt";
import {
  CLIENT_FILTER_KEY,
  CLIENT_REFETCH_KEY,
  CLIENT_SCROLL_KEY,
  getVatTypeOptions,
  getVatPercentageOptions,
  getValidVatPercentage,
  VatType,
} from "../clientDistributorTypes";
import { BusinessSettings } from "../../Settings/Settings";
import { getSettingsAPI } from "../../../Redux/settings";

interface Props {
  open: boolean;
  onClose: () => void;
  refreshData?: () => void;
  editData?: any;
}

// Module-level cache for Sage taxes to prevent flicker across modal open/close
let cachedSageTaxes: any[] = [];

export const CreateClientModal: React.FC<Props> = ({
  open,
  onClose,
  refreshData,
  editData,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote_id");
  const orderId = searchParams.get("order_id");
  const contextualParams = quoteId
    ? { quote_id: quoteId }
    : orderId
    ? { order_id: orderId }
    : undefined;
  const [form] = Form.useForm();
  const isSageSynced = !!(
    editData?.is_sage_synced ||
    (editData?.sage_id && editData?.sage_id !== "")
  );

  const userData = getDecryptedUserData();
  const [saving, setSaving] = useState(false);

  const [logoKey, setLogoKey] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);

  // --- Roles State ---
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  // Reference to current editData to avoid recreating callbacks
  const editDataRef = useRef(editData);
  editDataRef.current = editData;

  // --- Sage Taxes State (Pre-populated from cache or editData to avoid UI flicker) ---
  const [sageTaxes, setSageTaxes] = useState<any[]>(() => {
    if (cachedSageTaxes.length > 0) return cachedSageTaxes;
    if (
      editData?.tax_id &&
      typeof editData.tax_id === "object" &&
      editData.tax_id._id
    ) {
      return [editData.tax_id];
    }
    return [];
  });
  const [taxesLoading, setTaxesLoading] = useState<boolean>(
    () => cachedSageTaxes.length === 0,
  );
  // --- Salespersons State (Pagination + Search) ---
  const [salesPersons, setSalesPersons] = useState<any[]>([]);
  const [spLoading, setSpLoading] = useState(false);
  const [spPage, setSpPage] = useState(1);
  const [hasMoreSp, setHasMoreSp] = useState(true);
  const [searchText, setSearchText] = useState(""); // ✅ Track search query

  const [settingsData, setSettingsData] = useState<BusinessSettings | null>(
    null,
  );

  const fetchSettings = () => {
    dispatch(
      getSettingsAPI({
        onSuccess: (data: any) => {
          if (data?.business) {
            setSettingsData(data.business);
          } else {
            setSettingsData(null);
          }
        },
        onError: () => {},
      }),
    );
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // ✅ 1. Fetch Roles
  const fetchRolesData = useCallback(() => {
    setRolesLoading(true);
    dispatch(
      getRolesListAPI({
        filters: { role: "client" },
        onSuccess: (data: any) => {
          const list = data?.roles || [];
          const formatted = list.map((item: any) => ({
            label: item.name,
            value: item.key,
          }));
          setRoles(formatted);
          setRolesLoading(false);
        },
        onError: () => setRolesLoading(false),
      }),
    );
  }, [dispatch]);

  // ✅ 1b. Fetch Sage Taxes
  const fetchSageTaxes = useCallback(() => {
    if (cachedSageTaxes.length === 0) {
      setTaxesLoading(true);
    }
    dispatch(
      getSageTaxesAPI({
        onSuccess: (data: any) => {
          const list = Array.isArray(data) ? data : data?.list || [];
          cachedSageTaxes = list;
          setSageTaxes(list);
          setTaxesLoading(false);

          const currentEditData = editDataRef.current;
          if (currentEditData) {
            const currentTaxId =
              currentEditData.tax_id?._id || currentEditData.tax_id;
            const currentVatPct = currentEditData.vat_percentage;
            const match = list.find(
              (t: any) =>
                (currentTaxId && t._id === currentTaxId) ||
                (!currentTaxId &&
                  currentVatPct !== undefined &&
                  currentVatPct !== null &&
                  Number(t.tax_rate) === Number(currentVatPct)),
            );
            if (match) {
              if (form.getFieldValue("tax_id") !== match._id) {
                form.setFieldValue("tax_id", match._id);
              }
              if (form.getFieldValue("vat_percentage") !== match.tax_rate) {
                form.setFieldValue("vat_percentage", match.tax_rate);
              }
            }
          } else {
            if (!form.getFieldValue("tax_id")) {
              const findOldtax = list?.find(
                (tax: any) =>
                  tax?.name === "Std Rate" || String(tax?.code) === "1",
              );
              if (findOldtax) {
                form.setFieldValue("tax_id", findOldtax._id);
                form.setFieldValue("vat_percentage", findOldtax.tax_rate);
              }
            }
          }
        },
        onError: () => setTaxesLoading(false),
      }),
    );
  }, [dispatch, form]);

  // ✅ 2. Fetch Salespersons (Paginated & Searchable)
  const fetchSalesPersons = useCallback(
    (pageNo: number, search: string = "") => {
      setSpLoading(true);
      dispatch(
        getTeamMembersListAPI({
          filters: {
            roleKey: "sales",
            page: pageNo,
            limit: 20,
            search: search, // ✅ Pass search param to API
          },
          onSuccess: (res: any) => {
            const rawData = res?.data || [];
            const total = res?.total || 0;

            const formatted = rawData.map((item: any) => ({
              // Handle case where name might be encrypted or plain depending on your DB
              label:
                getDecryptedName(item) ||
                `${decryptByText(item.first_name)} ${decryptByText(
                  item.last_name,
                )}`,
              value: item._id,
            }));

            setSalesPersons((prev) => {
              // If page 1, replace data. If > 1, append data.
              return pageNo === 1 ? formatted : [...prev, ...formatted];
            });

            const currentLen =
              pageNo === 1
                ? rawData.length
                : salesPersons.length + rawData.length;

            // Check if we reached total count
            setHasMoreSp(currentLen < total);
            setSpLoading(false);
          },
          onError: () => setSpLoading(false),
        }),
      );
    },
    [dispatch, salesPersons.length],
  );

  // ✅ 3. Debounce Effect for Search
  // Triggers fetch when searchText changes, but waits 500ms
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (open) {
        setSpPage(1); // Reset to page 1 on new search
        setHasMoreSp(true); // Reset hasMore
        fetchSalesPersons(1, searchText);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchText, open, fetchSalesPersons]);

  // ✅ 4. Handle Search Input
  const handleOnSearch = (val: string) => {
    setSearchText(val); // This triggers the useEffect above
  };

  // ✅ 5. Handle Scroll for Pagination
  const handleSpPopupScroll = (e: any) => {
    const { target } = e;
    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      !spLoading &&
      hasMoreSp
    ) {
      const nextPage = spPage + 1;
      setSpPage(nextPage);
      fetchSalesPersons(nextPage, searchText); // Pass current search text
    }
  };

  useEffect(() => {
    if (open) {
      fetchRolesData();
      fetchSageTaxes();
      if (editData) {
        // ✅ 1. Manually Inject Current Salesperson
        // We construct a temporary option so the Select Input displays the Name (Label)
        // instead of the ID (Value) immediately.
        if (
          editData.sales_person &&
          typeof editData.sales_person === "object"
        ) {
          const sp = editData.sales_person;
          const injectedOption = {
            label:
              getDecryptedName(sp) ||
              `${decryptByText(sp.first_name)} ${decryptByText(sp.last_name)}`,
            value: sp._id,
          };
          // Set this as the initial list. The background fetch will append/update this later.
          setSalesPersons([injectedOption]);
        }

        // ✅ 2. Populate Form
        let initialTaxId = editData.tax_id?._id || editData.tax_id || null;
        const initialVatPct = getValidVatPercentage(editData.vat_percentage);

        // Pre-resolve tax_id from cached taxes if missing but vat_percentage exists
        if (!initialTaxId && initialVatPct !== null) {
          const taxesSource =
            cachedSageTaxes.length > 0 ? cachedSageTaxes : sageTaxes;
          const match = taxesSource.find(
            (t: any) => Number(t.tax_rate) === Number(initialVatPct),
          );
          if (match) {
            initialTaxId = match._id;
          }
        }

        form.setFieldsValue({
          companyName: editData.company_name,
          customer_type: editData.customer_type || "new",
          currency: editData.currency || "GBP",
          firstName: decryptByText(editData.first_name),
          lastName: decryptByText(editData.last_name),
          email: decryptByText(editData.email),
          // sage_account_contact_email: editData.sage_account_contact_email
          //   ? decryptByText(editData.sage_account_contact_email)
          //   : "",
          phone: editData.phone,
          type: editData?.role_data?.key || null,
          vat_type: editData.vat_type || VatType.NOT_VAT_REGISTERED,
          vat: editData.vat || "",
          vat_percentage: initialVatPct,
          tax_id: initialTaxId,
          // Handle both populated object or raw ID string
          salesperson: editData.sales_person?._id || editData.sales_person,
          discount: 25,
          margin: 0,
          allowLogin: editData.allowLogin ?? false,
          load_notice_phone: editData.phone_for_ln || null,
          load_notice_website: editData.website || null,
          customer_status:
            editData.customer_status != undefined &&
            editData.customer_status != null
              ? editData.customer_status
              : 1,
          show_retail_price: editData.show_retail_price ?? false,
        });
        setLogoKey(editData.client_load_notice_logo || "");
      } else {
        // Reset for Create Mode
        form.resetFields();
        setSearchText("");
        setSalesPersons([]);
        form.setFieldValue("allowLogin", false);
        // form.setFieldValue("sage_account_contact_email", "");
        form.setFieldValue("discount", 25);
        form.setFieldValue("margin", 0);
        form.setFieldValue("customer_type", "new");
        form.setFieldValue("currency", "GBP");
        form.setFieldValue("show_retail_price", false);
        const defaultTax = (
          cachedSageTaxes.length > 0 ? cachedSageTaxes : sageTaxes
        )?.find(
          (tax: any) => tax?.name === "Std Rate" || String(tax?.code) === "1",
        );
        form.setFieldValue("tax_id", defaultTax?._id || null);
        form.setFieldValue(
          "vat_percentage",
          defaultTax?.tax_rate !== undefined ? defaultTax.tax_rate : null,
        );
        setLogoKey("");
      }
    }
  }, [open, editData, fetchRolesData, fetchSageTaxes, form]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      let vatPercentage = values.vat_percentage;
      let taxId = values.tax_id || null;

      if (taxId && (vatPercentage === undefined || vatPercentage === null)) {
        const matched = sageTaxes.find((t: any) => t._id === taxId);
        if (matched) {
          vatPercentage = matched.tax_rate;
        }
      }

      if (
        !taxId &&
        vatPercentage !== undefined &&
        vatPercentage !== null &&
        vatPercentage !== ""
      ) {
        const matched = sageTaxes.find(
          (t: any) => Number(t.tax_rate) === Number(vatPercentage),
        );
        if (matched) {
          taxId = matched._id;
        }
      }

      const isVatRegistered = values.vat_type === VatType.VAT_REGISTERED;

      const payload: any = {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        // sage_account_contact_email: values.sage_account_contact_email,
        phone: values.phone,
        company_name: values.companyName,
        customer_type: values.customer_type,
        currency: values.currency,
        role: values.type,
        vat_type: values.vat_type,
        vat: isVatRegistered ? values.vat || "" : "",
        vat_percentage:
          vatPercentage !== undefined &&
          vatPercentage !== null &&
          vatPercentage !== ""
            ? Number(vatPercentage)
            : null,
        tax_id: taxId || null,
        sales_person: values.salesperson || null,
        discount: 25,
        margin: 0,
        client_load_notice_logo: logoKey,
        website: values.load_notice_website || null,
        phone_for_ln: values.load_notice_phone || null,
        show_retail_price: !!values.show_retail_price,

        ...(editData === null && { allowLogin: !!values.allowLogin }),
      };

      if (editData) {
        payload.customer_status = values.customer_status;
      }

      // console.log("payload", payload);
      // setSaving(false);
      // return;

      const apiAction = editData ? updateTeamMemberAPI : createTeamMemberAPI;
      if (editData) payload.id = editData._id;

      dispatch(
        apiAction({
          payload: payload,
          params: contextualParams,
          onSuccess: () => {
            sessionStorage.removeItem(CLIENT_FILTER_KEY);
            sessionStorage.removeItem(CLIENT_SCROLL_KEY);
            sessionStorage.setItem(CLIENT_REFETCH_KEY, "1");
            setSaving(false);
            onClose();
            refreshData?.();
          },
          onError: () => setSaving(false),
        }),
      );
    } catch (errorInfo) {
      console.log("Validation Failed:", errorInfo);
      setSaving(false);
    }
  };

  const statusColors: any = {
    1: "#419f20", // Active -> Green
    0: "#ff0004", // Inactive -> Red
    2: "#d0bc00", // On Hold -> Yellow
  };

  const getSageTaxOptions = (): any[] => {
    let list: any[] = [];
    if (sageTaxes.length > 0) {
      list = [...sageTaxes];
    } else if (cachedSageTaxes.length > 0) {
      list = [...cachedSageTaxes];
    }

    // If editData has a populated tax_id object that isn't in list yet, ensure it is available
    if (
      editData?.tax_id &&
      typeof editData.tax_id === "object" &&
      editData.tax_id._id &&
      !list.some((t: any) => t._id === editData.tax_id._id)
    ) {
      list.unshift(editData.tax_id);
    }

    if (list.length > 0) {
      return list.map((tax: any) => {
        const codeText = tax.code ? `Code : ${tax.code}` : "";
        const rateText = `${
          tax.tax_rate !== undefined && tax.tax_rate !== null ? tax.tax_rate : 0
        }%`;
        const nameText = tax.name || "";
        const termsText = tax.terms_description || tax.terms || "";
        const notionalText = tax.is_notional_acquisition_tax
          ? " [Notional]"
          : "";
        const fullSearch = `${rateText} ${nameText} ${codeText} ${termsText} ${notionalText}`;

        return {
          label: (
            <div>
              <span className="d-flex align-items-center gap-1">
                <span>{nameText}</span>
                <strong>{`(${rateText})`}</strong>
                {codeText && <span>{`(${codeText})`}</span>}
              </span>
            </div>
          ),
          filterLabel: fullSearch.toLowerCase(),
          value: tax._id,
          tax_id: tax._id,
          tax_rate: tax.tax_rate,
        };
      });
    }

    // Fallback if editData has tax_id (string ID or null) and vat_percentage
    if (
      editData &&
      (editData.tax_id || editData.vat_percentage !== undefined)
    ) {
      const currentTaxId = editData.tax_id?._id || editData.tax_id;
      const vatPct =
        editData.vat_percentage !== undefined ? editData.vat_percentage : 20;
      const isTwenty = Number(vatPct) === 20;
      const isZero = Number(vatPct) === 0;
      const name = isTwenty ? "Std Rate" : isZero ? "EC Zero SL" : "Tax";
      const code = isTwenty ? "1" : isZero ? "4" : "";
      const val =
        currentTaxId ||
        (vatPct !== undefined ? String(vatPct) : "tax_placeholder");

      return [
        {
          label: (
            <div>
              <span className="d-flex align-items-center gap-1">
                <span>{name}</span>
                <strong>{`(${vatPct}%)`}</strong>
                {code && <span>{`(Code : ${code})`}</span>}
              </span>
            </div>
          ),
          filterLabel: `${name} ${vatPct}% ${code}`.toLowerCase(),
          value: val,
          tax_id: val,
          tax_rate: vatPct,
        },
      ];
    }

    return getVatPercentageOptions().map((opt) => ({
      label: opt.label,
      value: String(opt.value),
      tax_id: null,
      tax_rate: opt.value,
      filterLabel: opt.label.toLowerCase(),
    }));
  };

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={svg?.closeIcon}
      width={"70rem"}
      className="commonModalClass createClientDistributors"
      title={
        <div className="text-center fs-4 fw-bolder lh-m mb-4">
          {editData ? "Update Client" : "Create Client"}
        </div>
      }
      rootClassName=" "
    >
      <Form
        layout="vertical"
        form={form}
        className="page-form"
        onFinish={onSave}
        initialValues={{
          vat_type: VatType.VAT_REGISTERED,
          
          allowLogin: false,
          show_retail_price: true,
          discount: 25,
          margin: 0,
        }}
      >
        {/* ... (Previous fields: Company Name, Names, Phone, Email) ... */}

        <div className="row gy-3 mb-4">
          {/* Company Name */}
          <div className="col-xl-3 col-sm-6">
            <Form.Item
              label={renderFormLabel("Company Name", true)}
              name="companyName"
              rules={[
                {
                  required: true,
                  message: "Company Name is required",
                  whitespace: true,
                },
              ]}
              required={false}
            >
              <Input
                placeholder="Enter Company Name"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div>

          {/* Customer Type */}
          <div className="col-xl-3 col-sm-6">
            <Form.Item
              label={renderFormLabel("Customer Type", true)}
              name="customer_type"
              rules={[
                {
                  required: true,
                  message: "Customer Type is required",
                },
              ]}
              required={false}
            >
              <Select
                placeholder="Select Customer Type"
                suffixIcon={svg?.selectDownArrow}
                options={[
                  { label: "Regular Customer", value: "regular" },
                  { label: "New Customer", value: "new" },
                ]}
                allowClear
              />
            </Form.Item>
          </div>

          <div className="col-xl-3 col-sm-6">
            <Form.Item
              label={renderFormLabel("First Name", true)}
              name="firstName"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "First Name is required",
                },
              ]}
              required={false}
            >
              <Input
                placeholder="Enter First Name"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div>

          <div className="col-xl-3 col-sm-6">
            <Form.Item
              label={renderFormLabel("Last Name", true)}
              name="lastName"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Last Name is required",
                },
              ]}
              required={false}
            >
              <Input
                placeholder="Enter Last Name"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div>

          <div className="col-4 col-md-4 col-sm-6">
            <Form.Item
              label={renderFormLabel("Phone", true)}
              name="phone"
              rules={[
                { required: true },
                { pattern: /^[0-9]+$/, message: "Invalid phone" },
              ]}
              required={false}
            >
              <Input
                placeholder="Enter Phone"
                type="tel"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div>

          <div className="col-4 col-md-4 col-sm-6">
            <Form.Item
              label={renderFormLabel("Customer Email", true)}
              name="email"
              rules={[
                { required: true, message: "Please enter customer email" },
                {
                  type: "email",
                  message: "Please enter a valid customer email",
                },
              ]}
              required={false}
            >
              <Input
                placeholder="Enter Customer Email"
                type="email"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div>

          {/* <div className="col-4 col-md-4 col-sm-6">
            <Form.Item
              label={renderFormLabel("Sage Account Contact Email", true)}
              name="sage_account_contact_email"
              rules={[
                {
                  required: true,
                  message: "Please enter sage account contact email",
                },
                {
                  type: "email",
                  message: "Please enter a valid sage account contact email",
                },
              ]}
              required={false}
            >
              <Input
                placeholder="Enter Sage Account Contact Email"
                type="email"
                className="form-control form-control-lg"
              />
            </Form.Item>
          </div> */}

          <div className="col-4 col-md-4 col-sm-6">
            <Tooltip
              title={
                editData?.is_order_sync
                  ? "Currency cannot be modified once customer order synced with Sage"
                  : isSageSynced && !isAdminSubAdminLoggedIn()
                  ? "Currency cannot be modified once synced with Sage"
                  : undefined
              }
            >
              <Form.Item
                label={renderFormLabel("Currency", true)}
                name="currency"
                rules={[
                  {
                    required: true,
                    message: "Currency is required",
                  },
                ]}
                required={false}
              >
                <Select
                  placeholder="Select Currency"
                  suffixIcon={svg?.selectDownArrow}
                  options={getCurrencyOptions()}
                  disabled={
                    (isSageSynced && !isAdminSubAdminLoggedIn()) ||
                    editData?.is_order_sync
                  }
                  allowClear={!isSageSynced}
                />
              </Form.Item>
            </Tooltip>
          </div>

          <div className="col-4 col-md-4 col-sm-6">
            <Form.Item
              label={renderFormLabel("Type", true)}
              name="type"
              rules={[{ required: true }]}
              required={false}
            >
              <Select
                placeholder="Select Type"
                suffixIcon={svg?.selectDownArrow}
                options={roles}
                loading={rolesLoading}
                allowClear
              />
            </Form.Item>
          </div>

          <div className="col-4 col-md-4 col-sm-6">
            <Form.Item
              label={renderFormLabel("VAT Type", true)}
              name="vat_type"
              rules={[{ required: true, message: "VAT Type is required" }]}
              required={false}
            >
              <Select
                placeholder="Select VAT Type"
                suffixIcon={svg?.selectDownArrow}
                options={getVatTypeOptions()}
                allowClear
                onChange={() => {
                  form.setFieldValue("vat", "");
                }}
              />
            </Form.Item>
          </div>

          <Form.Item noStyle dependencies={["vat_type"]}>
            {({ getFieldValue }) =>
              getFieldValue("vat_type") === VatType.VAT_REGISTERED && (
                <div className="col-4 col-md-4 col-sm-6">
                  <Form.Item
                    label={renderFormLabel("VAT Number", false)}
                    name="vat"
                    // rules={[{ required: true, whitespace: true }]}
                    required={false}
                  >
                    <Input
                      placeholder="Enter VAT Number"
                      className="form-control form-control-lg"
                    />
                  </Form.Item>
                </div>
              )
            }
          </Form.Item>

          <div className="col-4 col-md-4 col-sm-6">
            <Form.Item
              label={renderFormLabel("VAT Percentage", true)}
              name="tax_id"
              rules={[
                { required: true, message: "VAT Percentage is required" },
              ]}
              required={false}
            >
              <Select
                placeholder="Select VAT Percentage"
                suffixIcon={svg?.selectDownArrow}
                options={getSageTaxOptions()}
                loading={taxesLoading}
                allowClear={false}
                showSearch
                filterOption={(input, option: any) =>
                  String(option?.filterLabel || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={(val, option: any) => {
                  form.setFieldValue("tax_id", val || null);
                  form.setFieldValue(
                    "vat_percentage",
                    val && option?.tax_rate !== undefined
                      ? option.tax_rate
                      : null,
                  );
                }}
              />
            </Form.Item>
            <Form.Item name="vat_percentage" noStyle>
              <Input type="hidden" />
            </Form.Item>
          </div>

          <div className="col-4 col-md-4 col-sm-6">
            <Form.Item
              label={renderFormLabel("Salesperson", false)}
              name="salesperson"
            >
              <Select
                placeholder="Select Salesperson"
                suffixIcon={svg?.selectDownArrow}
                allowClear
                options={[
                  ...getStaticSalespersonOptions().filter((opt) =>
                    opt.label
                      .toLowerCase()
                      .includes((searchText || "").trim().toLowerCase()),
                  ),
                  ...salesPersons,
                ]}
                loading={spLoading}
                // ✅ BACKEND SEARCH CONFIGURATION
                showSearch
                onSearch={handleOnSearch}
                onClear={() => setSearchText("")}
                onChange={() => setSearchText("")}
                onPopupScroll={handleSpPopupScroll}
                filterOption={false} // 👈 Important: Disables client-side filtering
                notFoundContent={
                  spLoading ? <Spin size="small" /> : "No Salesperson Found"
                }
              />
            </Form.Item>
          </div>

          <div
            className="col-4 col-md-4 col-sm-6 d-none"
            style={{ display: "none" }}
          >
            <Form.Item
              label={renderFormLabel("Discount", true)}
              name="discount"
              validateFirst
              required={false}
              rules={[
                { required: true, message: "Discount is required" },
                {
                  validator: async (_, value) => {
                    // 1. Handle empty (handled by required rule)
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }

                    const numValue = Number(value);

                    // 2. Check Range (0 - 100)
                    if (numValue < 0) {
                      return Promise.reject(new Error("Cannot be negative"));
                    }
                    if (numValue > 100) {
                      return Promise.reject(new Error("Cannot exceed 100%"));
                    }

                    // 3. Check Decimal Precision (Max 2 decimal places)
                    // Regex: Allows integers and decimals up to 2 places
                    // (Changed {1,2} to {0,2} to allow typing "10." without error)
                    if (!/^\d+(\.\d{0,2})?$/.test(value.toString())) {
                      return Promise.reject(new Error("Max 2 decimal places"));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="0"
                type="number"
                min={0}
                max={100}
                prefix={<span className="text-white">%</span>}
                className="form-control form-control-lg"
                // Block invalid keys for number inputs
                onKeyDown={(e) => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e: any) => e.currentTarget.blur()}
                // Prevent pasting invalid text
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text");
                  if (!/^[0-9.]+$/.test(text)) {
                    e.preventDefault();
                  }
                }}
                disabled={!isAdminSubAdminLoggedIn()}
              />
            </Form.Item>
          </div>
          <div
            className="col-4 col-md-4 col-sm-6 d-none"
            style={{ display: "none" }}
          >
            <Form.Item
              label={renderFormLabel("Markup", true)}
              name="margin"
              validateFirst
              required={false}
              rules={[
                {
                  validator: async (_, value) => {
                    if (
                      value === undefined ||
                      value === null ||
                      value === "" ||
                      (typeof value === "string" && value.trim() === "")
                    ) {
                      return Promise.reject(new Error("Margin is required"));
                    }

                    const numValue = Number(value);

                    if (isNaN(numValue)) {
                      return Promise.reject(
                        new Error("Margin must be a number"),
                      );
                    }

                    if (numValue < 0) {
                      return Promise.reject(new Error("Cannot be negative"));
                    }

                    if (!/^\d+(\.\d{0,2})?$/.test(value.toString())) {
                      return Promise.reject(new Error("Max 2 decimal places"));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="0"
                type="number"
                min={0}
                prefix={<span className="text-white">%</span>}
                className="form-control form-control-lg"
                onKeyDown={(e) => {
                  if (["e", "E", "-", "+"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e: any) => e.currentTarget.blur()}
                onPaste={(e) => {
                  const text = e.clipboardData.getData("text");
                  if (!/^[0-9.]+$/.test(text)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </div>
          <div className="col-4 col-md-4 col-sm-6">
            <Form.Item
              label={renderFormLabel("Show Retail Price", false)}
              name="show_retail_price"
              validateFirst
              required={false}
              valuePropName="checked"
            >
              <Switch
                className="customSwitch"
                checkedChildren="Yes"
                unCheckedChildren="No"
              />
            </Form.Item>
          </div>
          {editData &&
          [ROLE?.ADMIN, ROLE?.SUB_ADMIN]?.includes(userData?.role?.key) ? (
            <div className="col-4 col-md-4 col-sm-6">
              <Form.Item
                label={renderFormLabel("Status", false)}
                name="customer_status"
              >
                <Select
                  placeholder="Select Status"
                  suffixIcon={svg?.selectDownArrow}
                  filterOption={false} // 👈 Important: Disables client-side filtering
                  options={[
                    {
                      label: (
                        <span style={{ color: statusColors[1] }}>Active</span>
                      ),
                      value: 1,
                    },
                    {
                      label: (
                        <span style={{ color: statusColors[0] }}>Inactive</span>
                      ),
                      value: 0,
                    },
                    {
                      label: (
                        <span style={{ color: statusColors[2] }}>On Hold</span>
                      ),
                      value: 2,
                    },
                  ]}
                />
              </Form.Item>
            </div>
          ) : (
            ""
          )}
        </div>

        <div className="row gy-3 mb-4">
          {/* --- INTEGRATED S3 UPLOADER --- */}

          <Form.Item noStyle dependencies={["type"]}>
            {({ getFieldValue }) => {
              const isDistributor = getFieldValue("type") === "distributor";
              return (
                <>
                  {/* --- INTEGRATED S3 UPLOADER --- */}
                  <div
                    className={isDistributor ? "col-md-6 col-12" : "col-md-6"}
                  >
                    <div className="form-item h-100">
                      <label
                        className="mb-1 fw-semibold"
                        style={{ fontSize: 14 }}
                      >
                        Company Logo
                      </label>
                      <div
                        className="createProductsimageUploadBox mb-sm-0 position-relative border rounded p-2 w-100"
                        style={{
                          minHeight: isDistributor
                            ? "calc(100% - 28px)"
                            : "142px",
                        }}
                      >
                        <S3Uploader
                          withThumbnail={false}
                          folder="logos"
                          disabled={logoUploading}
                          image={logoKey ? getFullAwsUrl(logoKey) : undefined}
                          showIcons={true}
                          onRemove={() => setLogoKey("")}
                          onUploadStart={() => setLogoUploading(true)}
                          onUploaded={(url: string, key: string) => {
                            setLogoUploading(false);
                            setLogoKey(key);
                          }}
                          onUploadError={() => setLogoUploading(false)}
                          imageDisplayClass={"contain"}
                        >
                          <div
                            className="d-flex flex-column align-items-center justify-content-center w-100 h-100 gap-2"
                            style={{ minHeight: "70px" }}
                          >
                            {logoUploading ? (
                              <div className="text-primary fw-bold">
                                Uploading...
                              </div>
                            ) : (
                              <>
                                <span className="svg-32 text-muted">
                                  {svg?.imageUpload}
                                </span>
                                <div style={{ marginTop: 10, fontWeight: 600 }}>
                                  Upload Logo
                                </div>
                                <div style={{ fontSize: 12, color: "#999" }}>
                                  Click or drag file to upload
                                </div>
                              </>
                            )}
                          </div>
                        </S3Uploader>
                      </div>
                    </div>
                  </div>

                  {isDistributor && (
                    <div className="col-md-6 col-12">
                      <div className="row gy-3">
                        <div className="col-12">
                          <Form.Item
                            label={renderFormLabel(
                              "Phone Number for LN",
                              false,
                            )}
                            name="load_notice_phone"
                            required={false}
                          >
                            <Input
                              placeholder="Enter Phone Number for LN"
                              className="form-control form-control-lg"
                            />
                          </Form.Item>
                        </div>
                        <div className="col-12">
                          <Form.Item
                            label={renderFormLabel("Website for LN", false)}
                            name="load_notice_website"
                            required={false}
                          >
                            <Input
                              placeholder="Website for LN"
                              className="form-control form-control-lg"
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            }}
          </Form.Item>
        </div>

        {/* ✅ CORRECTED: Allow Login Switch */}
        {editData === null && (
          <div className="row gy-3 mb-4">
            <div className="col-12 mt-4">
              <div className="d-flex align-items-center justify-content-between">
                {/* 1. The Label is OUTSIDE the Form.Item */}
                <span className="fs-6 fw-semibold text-primary">
                  Login Access{" "}
                  <small className="fw-normal text-muted">
                    (It will send email on Save)
                  </small>
                </span>

                {/* 2. Form.Item strictly wraps ONLY the Switch */}
                <Form.Item
                  name="allowLogin"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }} // Removes default Antd bottom margin for perfect vertical alignment
                >
                  <Switch className="customSwitch" />
                </Form.Item>
              </div>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-center gap-3 mt-4">
          <button
            onClick={onClose}
            className="btn btn-lg btn-outline-secondary h-auto px-5"
            type="button"
          >
            CLOSE
          </button>
          <Button
            htmlType="submit"
            loading={saving}
            className="btn btn-lg btn-primary h-auto px-5"
          >
            {editData ? "UPDATE" : "SAVE"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
